import axios from "axios";
import { auth, getAppCheckToken } from "@/service/firebase";

const CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;
const ERROR_RETRY_DELAY_MS = 5000;
const MAX_RETRY_AFTER_MS = 60000;

let memoryCache = null;
let cacheGeneration = 0;
const pendingRequests = new Map();
const failedRequests = new Map();

function retryDelay(error) {
  if (error?.response?.status !== 429) return ERROR_RETRY_DELAY_MS;
  const value = error.response.headers?.["retry-after"];
  // Retry-After pode não ser exposto pelo CORS da API legada.
  if (value == null || value === "") return MAX_RETRY_AFTER_MS;
  const seconds = Number(value);
  const delay = Number.isFinite(seconds)
    ? seconds * 1000
    : Date.parse(value) - Date.now();
  return Number.isFinite(delay)
    ? Math.max(ERROR_RETRY_DELAY_MS, Math.min(delay, MAX_RETRY_AFTER_MS))
    : MAX_RETRY_AFTER_MS;
}

function normalizedBaseUrl(value) {
  return String(value || "").replace(/\/+$/, "");
}

export function managementApiBaseUrl() {
  const configured = normalizedBaseUrl(process.env.VUE_APP_MANAGEMENT_API_URL);
  if (configured) return configured;

  const hostname = window.location.hostname;
  if (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "cabine-teste.web.app"
  ) {
    return "https://teste.photonowoficial.com.br/v1";
  }
  return "https://photonow.com.br/v1";
}

function createClient() {
  return axios.create({
    baseURL: managementApiBaseUrl(),
    timeout: 12000,
    headers: { "Content-Type": "application/json" },
  });
}

export async function fetchTotemDeviceConfig(totemId, options = {}) {
  if (!totemId) return null;

  if (
    !options.force &&
    memoryCache &&
    memoryCache.totemId === totemId &&
    Date.now() - memoryCache.loadedAt < CONFIG_CACHE_TTL_MS
  ) {
    return memoryCache.value;
  }

  // Restaurar a sessão no boot não é uma falha da API: não aplicar espera
  // que impeça o watcher do ID de consultar logo após o login do Firebase.
  const user = auth.currentUser;
  if (!user) throw new Error("Sessão Firebase do totem ausente.");

  // Suporte, Wi-Fi e troca de rota compartilham a consulta, inclusive force.
  // O force ignora só o cache válido; não ignora uma recusa/rate limit recente.
  if (pendingRequests.has(totemId)) return pendingRequests.get(totemId);
  const failure = failedRequests.get(totemId);
  if (failure && Date.now() < failure.retryAt) throw failure.error;

  const generation = cacheGeneration;
  const request = (async () => {
    const [idToken, appCheckToken] = await Promise.all([
      user.getIdToken(),
      getAppCheckToken(),
    ]);
    const headers = { Authorization: `Bearer ${idToken}` };
    if (appCheckToken) headers["X-Firebase-AppCheck"] = appCheckToken;

    const response = await createClient().get(
      `/totem/device/config/${encodeURIComponent(totemId)}`,
      { headers }
    );
    if (response.data.totemId !== totemId) {
      throw new Error("A API devolveu a configuração de outro totem.");
    }
    if (generation === cacheGeneration) {
      memoryCache = {
        totemId,
        loadedAt: Date.now(),
        value: response.data,
      };
      failedRequests.delete(totemId);
    }
    return response.data;
  })();
  pendingRequests.set(totemId, request);
  try {
    return await request;
  } catch (error) {
    if (generation === cacheGeneration) {
      failedRequests.set(totemId, {
        error,
        retryAt: Date.now() + retryDelay(error),
      });
    }
    throw error;
  } finally {
    if (pendingRequests.get(totemId) === request)
      pendingRequests.delete(totemId);
  }
}

export function resetTotemDeviceConfigCache() {
  cacheGeneration += 1;
  memoryCache = null;
  pendingRequests.clear();
  failedRequests.clear();
}
